import { getSpotFromSearch } from '@canvas/branch-deploy';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { type RelaySupplierResult } from '../network/socket';
import {
  type Transport,
  type TransportCallbacks,
  type TransportContext,
  createWebSocketTransport,
} from '../transport';

import { type CollabStrategy } from './collab-strategy';

export class CfCollabStrategy implements CollabStrategy {
  readonly type = 'cf' as const;

  createTransport(ctx: TransportContext, callbacks: TransportCallbacks): Transport {
    return createWebSocketTransport(ctx, callbacks);
  }

  sendAwareness(
    message: Uint8Array<ArrayBuffer>,
    _transport: Transport | undefined,
    fallbackSend: (msg: Uint8Array<ArrayBuffer>) => void
  ): void {
    fallbackSend(message);
  }

  getStepsUrl(params: {
    documentId: string;
    headStep: string;
    serverUrl?: string;
    attempt: number;
  }): string {
    const { documentId, headStep, attempt } = params;
    const relayBaseUrl = this.getCanvasRelayBaseUrl(attempt);
    return `${relayBaseUrl}/api/public/document/${encodeURIComponent(
      documentId
    )}/steps/${headStep}`;
  }

  async getRelayUrl(params: {
    boardId: string;
    attempt: number;
    accessToken: string;
  }): Promise<RelaySupplierResult> {
    const { boardId, attempt, accessToken } = params;

    const relaySpot =
      typeof globalThis.location !== 'undefined'
        ? getSpotFromSearch(globalThis.location.search, 'canvas-relay')
        : undefined;

    const relayUrl =
      typeof relaySpot === 'number'
        ? `wss://canvas-relay-canvas-relay-${relaySpot}.atlassian-workers-test.workers.dev`
        : process.env.VITE_CANVAS_RELAY_WSS_URL;

    let baseUrl: string | undefined;
    let prefix = '';
    if (
      isFeatureFlagEnabled('useConnectionFallback') &&
      attempt > 2 &&
      typeof globalThis.location !== 'undefined'
    ) {
      const parentUrl = new URL(globalThis.location.href);
      const wsProtocol = parentUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      baseUrl = `${wsProtocol}//${parentUrl.host}`;
      prefix = '/canvas-collab/backup';
    } else {
      baseUrl = relayUrl;
    }

    if (!baseUrl) {
      throw new Error('No relay URL found in current environment');
    }

    const url = new URL(baseUrl);
    url.pathname = `${prefix}/api/public/document/${encodeURIComponent(boardId)}/ws`;
    url.searchParams.set('closeOnMissingUpdate', 'true');

    return {
      url: url.href,
      accessToken,
    };
  }

  shouldRefreshRelayDetails(): boolean {
    return false;
  }

  private getCanvasRelayBaseUrl(attempt: number): string | undefined {
    const relaySpot =
      typeof globalThis.location !== 'undefined'
        ? getSpotFromSearch(globalThis.location.search, 'canvas-relay')
        : undefined;

    if (
      isFeatureFlagEnabled('useConnectionFallback') &&
      attempt > 2 &&
      typeof globalThis.location !== 'undefined'
    ) {
      const parent = new URL(globalThis.location.href);
      const base = `${parent.protocol}//${parent.host}`;
      return `${base}/canvas-collab/backup`;
    }

    const relayBaseUrl =
      typeof relaySpot === 'number'
        ? `https://canvas-relay-canvas-relay-${relaySpot}.atlassian-workers-test.workers.dev`
        : process.env.VITE_CANVAS_RELAY_URL;

    return relayBaseUrl;
  }
}
